import React, { type FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { AvatarProps } from '@atlaskit/avatar-group';
import type { BackgroundColor, TextColor } from '@atlaskit/primitives';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import type { ContentMode } from '../presenceTypes';

import { EditIcon } from './EditIcon';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown as a title of a section inside of a menu. The people in the section are editing a page',
	},
	viewing: {
		id: 'team-presence.menu.viewing',
		defaultMessage: 'Viewing',
		description: 'The text is used to indicate that the current user is `Viewing the page`',
	},
});

const containerStyles = xcss({
	position: 'relative',
});

const avatarBadgeStyles = xcss({
	position: 'absolute',
	bottom: '-2px',
	right: '-2px',
	height: '12px',
	width: '12px',
	borderRadius: '3px',
	color: 'color.text.inverse', // TODO: This will need to be swapped for the foregroundColor, based on sessionId
	boxShadow: 'elevation.shadow.overflow',
	fontWeight: token('font.weight.semibold'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '10px',
	zIndex: 'card',
});

const EditorAvatarBadge: FC<
	Pick<PresenceAvatarProps, 'borderColor' | 'badgeTextColor' | 'contentMode' | 'name'>
> = ({ borderColor: backgroundColor, badgeTextColor, contentMode, name }) => {
	const shouldRenderEditIcon = contentMode === 'view';
	const firstInitial = name?.substring(0, 1);

	/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values,@atlaskit/design-system/consistent-css-prop-usage,@atlaskit/ui-styling-standard/no-unsafe-values */
	return (
		<Flex
			testId="editor-avatar-badge"
			alignItems="center"
			justifyContent="center"
			xcss={[
				avatarBadgeStyles,
				xcss({
					backgroundColor: backgroundColor as BackgroundColor,
					color: badgeTextColor as TextColor,
				}),
			]}
		>
			{shouldRenderEditIcon ? (
				<EditIcon testId="editor-avatar-badge-icon" aria-label="editor" />
			) : (
				<Box testId="editor-avatar-badge-initial" as="span">
					{firstInitial}
				</Box>
			)}
		</Flex>
	);
	/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values,@atlaskit/design-system/consistent-css-prop-usage,@atlaskit/ui-styling-standard/no-unsafe-values */
};

export type PresenceAvatarProps = AvatarProps & {
	activity: PresenceActivity;
	badgeTextColor: string;
	contentMode: ContentMode;
	contentId: string;
	contentType: string;
};

export const PresenceAvatar = ({
	activity,
	badgeTextColor,
	contentMode,
	contentId,
	contentType,
	name,
	...avatarProps
}: PresenceAvatarProps) => {
	const { formatMessage } = useIntl();

	const activityText =
		activity === 'editor' ? formatMessage(i18n.editing) : formatMessage(i18n.viewing);
	const label = `${name} (${activityText})`;

	return (
		<Tooltip content={label}>
			<Flex direction="column" alignItems="center" gap="space.0" xcss={containerStyles}>
				<Avatar {...avatarProps} appearance="circle" size="small" name={name} label={label} />
				{activity === 'editor' && (
					<EditorAvatarBadge
						borderColor={avatarProps.borderColor}
						badgeTextColor={badgeTextColor}
						contentMode={contentMode}
						name={name}
					/>
				)}
			</Flex>
		</Tooltip>
	);
};
